const MONTH_INDEX = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

const normalizeLabel = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const formatDate = (value) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getPeriodRange = ({ period, customDateRange, now = new Date() }) => {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (period === 'custom') {
    if (!customDateRange?.startDate || !customDateRange?.endDate) {
      return null;
    }
    const start = new Date(customDateRange.startDate);
    const end = new Date(customDateRange.endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return null;
    }
    return { start, end };
  }

  switch (period) {
    case 'last_3_days': {
      const start = new Date(today);
      start.setDate(start.getDate() - 3);
      return { start, end: today };
    }
    case 'last_7_days': {
      const start = new Date(today);
      start.setDate(start.getDate() - 7);
      return { start, end: today };
    }
    case 'this_month':
      return { start: new Date(today.getFullYear(), today.getMonth(), 1), end: today };
    case 'last_month':
      return {
        start: new Date(today.getFullYear(), today.getMonth() - 1, 1),
        end: new Date(today.getFullYear(), today.getMonth(), 0),
      };
    case 'this_year':
      return { start: new Date(today.getFullYear(), 0, 1), end: today };
    case 'last_year':
      return {
        start: new Date(today.getFullYear() - 1, 0, 1),
        end: new Date(today.getFullYear() - 1, 11, 31),
      };
    default:
      return null;
  }
};

export const findOptionByLabel = (options = [], label, keys = ['name', 'label', 'description']) => {
  const target = normalizeLabel(label);
  if (!target) {
    return null;
  }

  return (
    options.find((option) =>
      keys.some((key) => {
        const candidate = normalizeLabel(option?.[key]);
        if (!candidate) {
          return false;
        }
        return candidate === target || candidate.includes(target) || target.includes(candidate);
      })
    ) || null
  );
};

export const buildMonthlyDrilldownRange = ({ period, customDateRange, monthLabel, now = new Date() }) => {
  const monthToken = normalizeLabel(monthLabel).slice(0, 3);
  const monthIndex = MONTH_INDEX[monthToken];
  if (monthIndex == null) {
    return { dateRange: null, note: 'Month drilldown is unavailable for this chart point.' };
  }

  const activeRange = getPeriodRange({ period, customDateRange, now });
  if (!activeRange) {
    return { dateRange: null, note: 'Month drilldown is only available for dated periods.' };
  }

  if (activeRange.start.getFullYear() !== activeRange.end.getFullYear()) {
    return { dateRange: null, note: 'Month drilldown is only available for single-year periods.' };
  }

  const year = activeRange.start.getFullYear();
  const monthStart = new Date(year, monthIndex, 1);
  const monthEnd = new Date(year, monthIndex + 1, 0);
  const start = monthStart > activeRange.start ? monthStart : activeRange.start;
  const end = monthEnd < activeRange.end ? monthEnd : activeRange.end;

  if (start > end) {
    return { dateRange: null, note: `${monthLabel} is outside the active period.` };
  }

  return {
    dateRange: {
      startDate: formatDate(start),
      endDate: formatDate(end),
    },
    note: null,
  };
};